import _ from 'lodash'
import Vue from 'vue'
import Vuex from 'vuex'
import api from '@/lib/api'

Vue.use(Vuex)

export default new Vuex.Store({
	state: {
		initialized: false,

		init: {
			initialized: false,
			statuses: {
				invoices: {},
				applications: {},
				users: { types: {}, statuses: {} },
				quotes: {},
				payments: {}
			},
			applications: {
				types: ['new'],
				slugs: {},
				statuses: {}
			},
			amendments: {
				types: {}
			},
			users: {
				statuses: {},
				types: []
			},
			system: {
				web: '#',
				env: '#',
				url: '#'
			},
			address: {
				allowed_countries: {},
				countries: {},
				states: {}
			},
			notifications: {
				triggers: []
			},
			disks: {
				local: {
					name: 'local',
					disk: 'local'
				},
				application: {
					name: 'application',
					disk: 'test'
				},
				quote: {
					name: 'quote',
					disk: 'local'
				},
				payment: {
					name: 'payment',
					disk: 'local'
				},
				user: {
					name: 'user',
					disk: 'local'
				},
				carrier: {
					name: 'carrier',
					disk: 'local'
				},
				uppy_temp: 'local'
			},
			settings: {
				billing: {
					paypal_fee_enabled: null,
					stripe_fee_enabled: null,
					plaid_fee_enabled: null
				}
			}
		},
		schemaUpdate: new Date(),

		form: {},

		application: {
			id: null,
			status: 1,
			currentStep: null, // MUST be null and set after loaded
			currentSubStep: false, // MUST be false when not in use
			submitted: false, // if this changed, the user submitted the form (first time/again)
			completedSubSteps: {},
			excludedSubSteps: [],
			values: {},
			errors: {},
			// Added to track invalid data for non-formulate fields
			// Using b-* fields instead because it's faster without input lag
			// but we need to track which ones are invalid
			// will be {fieldname: error} which is set manually
			// this should be empty in order to "continue"
			invalid: {},
			// Tracks which fields have been modified,
			// helps determine which to show the field validation feedback
			invalidDirty: [],
			// Tracks any special variables that might influence how the schema is rendered
			// Any values added should have the defaults listed here (and below)
			config: {
				effective_date_auto_complete: true
			}
		},

		filters: {
			applications: {
				search: null,
				status: null,
				quote_status: null,
				handled_by: null,
				type: null,
				has_quote: null,
				has_invoice: null,
				has_payment: null,
				has_renewal: null,
				is_renewal: null,
				has_ai: null,
				archived: null,
				has_policies: null,
				has_policy_dates: null,
				pin_rush_order: '1',
				trashed: null,
				date_range: { start: null, end: null },
				date_field: null
			}
		},

		api: {
			carriers: [],
			products: [],
			taxes: [],
			coverages: [],
			templates: []
		}
	},

	modules: {
		//
	},

	mutations: {
		resetInit (state, init) {
			state.initialized = false
			state.init = init
		},
		setInit (state, init) {
			state.init = init
		},
		setFormInfo (state, info) {
			state.form = info
		},
		triggerSchemaUpdate (state) {
			state.schemaUpdate = new Date()
		},
		setValues (state, values) {
			state.application['values'] = values
		},
		setErrors (state, errors) {
			state.application['errors'] = errors
		},
		setInvalid (state, { field, error }) {
			Vue.set(state.application.invalid, field, error)
		},
		setInvalidDirty (state, fieldName) {
			if (! state.application.invalidDirty.includes(fieldName)) {
				state.application.invalidDirty.push(fieldName)
			}
		},
		unsetInvalid (state, field) {
			Vue.delete(state.application.invalid, field)
			Vue.delete(state.application.invalidDirty, field)
		},
		setCurrentStep (state, currentStep) {
			state.application['currentStep'] = currentStep
		},
		setCurrentSubStep (state, currentSubStep) {
			state.application['currentSubStep'] = currentSubStep
		},
		setCompletedSubSteps (state, completedSubSteps) {
			/**
			 * Developer Comments:
			 * This loop shouldn't be needed here, but without it.. laravel mix breaks.
			 */
			for (const completedSubStepsKey in completedSubSteps) {
				if (!completedSubSteps.hasOwnProperty(completedSubStepsKey)) continue
				state.application['completedSubSteps'][completedSubStepsKey] = completedSubSteps[completedSubStepsKey]
			}
		},
		setExcludedSubSteps (state, excludedSubSteps) {
			state.application['excludedSubSteps'] = excludedSubSteps
		},
		setLastStep (state, lastStep) {
			state.application['lastStep'] = lastStep
		},
		setApplicationId (state, appId) {
			state.application['id'] = appId
		},
		setApplicationStatusId (state, status_id) {
			state.application['status'] = status_id
		},
		saveFilters (state, filter) {
			state.filters[filter.key] = filter.filters
			localStorage.setItem('filters', JSON.stringify(state.filters))
		},
		setApiData: (state, values) => {
			state.initialized = true
			state.api[values.key] = values.data ?? []
		},
		setApplicationSubmitted (state) {
			Vue.set(state.application, 'submitted', new Date())
		},
		setConfig (state, config) {
			if (! state.application.hasOwnProperty('config')) return
			if (! config.hasOwnProperty('key')) return
			if (! config.hasOwnProperty('val')) return
			state.application['config'][config['key']] = config['val']
		}
	},

	getters: {
		applicationId: state => {
			return state.application.id
		},
		applicationStatusId: state => {
			return state.application.status
		},
		appValues: state => {
			return state.application.values
		},
		appErrors: state => {
			return state.application.errors
		},
		getFilters: state => {
			let lastFilters = localStorage.getItem('filters')

			if (lastFilters) {
				state.filters = JSON.parse(lastFilters)

				return JSON.parse(lastFilters)
			}

			return state.filters
		},
		getFormInfo: state => {
			return state.form
		},
		getFormFieldOptions: (state) => (fieldName) => {
			const fieldOptions = _.filter(state.form.options, { 'field': fieldName })

			if (! fieldOptions || fieldOptions.length === 0) {
				return null
			}

			fieldOptions:
			for (const fieldOptionsKey in fieldOptions) {
				if (!fieldOptions.hasOwnProperty(fieldOptionsKey)) continue
				const fieldOption = fieldOptions[fieldOptionsKey]

				if (! fieldOption.hasOwnProperty('options')) {
					return null
				}

				// Check if this option has any conditions
				let meetsConditions = false
				if (! fieldOption.hasOwnProperty('conditions')) {
					meetsConditions = true
				} else {
					if (! Object.keys(fieldOption.conditions).length) {
						meetsConditions = true
					}
				}

				for (const conditionsKey in fieldOption.conditions) {
					if (!fieldOption.conditions.hasOwnProperty(conditionsKey)) continue
					let validCondition = false
					const condition = fieldOption.conditions[conditionsKey]

					let appValue = null
					if (state.application.values.hasOwnProperty(condition.field_name)) {
						appValue = state.application.values[condition.field_name]
					}

					let deepValue = _.findValueDeep(state.application.values, condition.field_name, { leavesOnly: false })
					if (deepValue && deepValue.hasOwnProperty(condition.field_name)) {
						appValue = deepValue[condition.field_name]
					}

					// Value not found in application
					// No point in checking the other similar options
					if (! appValue) {
						continue fieldOptions
					}

					if (condition.comparison === '=') {
						// This is non-specific on purpose
						validCondition = appValue == condition.value
					}

					if (condition.comparison === '!=') {
						// This is non-specific on purpose
						validCondition = appValue != condition.value
					}

					let objLength = 0
					let isObject = typeof appValue === 'object'

					if (isObject) {
						objLength = Object.keys(appValue).length >= condition.value
					}

					if (condition.comparison === '>=') {
						if (isObject) {
							validCondition = objLength >= condition.value
						}

						validCondition = parseFloat(appValue) >= parseFloat(condition.value)
					}

					if (condition.comparison === '<=') {
						if (isObject) {
							validCondition = objLength <= condition.value
						}

						validCondition = parseFloat(state.application.values[condition.field_name]) <= parseFloat(condition.value)
					}

					if (condition.comparison === '<') {
						if (isObject) {
							validCondition = objLength < condition.value
						}

						validCondition = parseFloat(appValue) < parseFloat(condition.value)
					}

					if (condition.comparison === '>') {
						if (isObject) {
							validCondition = objLength > condition.value
						}

						validCondition = parseFloat(appValue) > parseFloat(condition.value)
					}

					if (condition.comparison === 'includes') {
						if (isObject) {
							validCondition = appValue.includes(condition.value)
						}
					}

					if (condition.comparison === 'not_includes') {
						if (isObject) {
							validCondition = appValue.includes(condition.value) === false
						}
					}

					/**
					 * If this condition is valid then we can check if we should toggle meetsConditions
					 * Just keeping this simple, no grouped conditions here
					 */
					if (condition.hasOwnProperty('logical')) {
						if (condition.logical === 'OR' && validCondition) { meetsConditions = true }
						if (condition.logical === 'OR' && !validCondition && meetsConditions) { meetsConditions = true }

						if (condition.logical === 'AND' && !validCondition) { meetsConditions = false }
						if (condition.logical === 'AND' && validCondition && meetsConditions) { meetsConditions = true }
						if (condition.logical === 'AND' && validCondition && !meetsConditions) { meetsConditions = false }
					} else {
						if (validCondition) { meetsConditions = true }
					}
				}

				if (! meetsConditions) {
					continue
				}

				let options = {}
				_.forOwn(fieldOption.options,function (item) {
					options[item['value']] = item['name']
				})

				// Return sorted alphabetically
				return Object.keys(options).sort().reduce((result, key) => {
					result[key] = options[key]
					return result
				}, {})
			}
		},
		invalidFieldState: (state) => (fieldName) => {
			if (! state.application.invalidDirty.includes(fieldName)) {
				return null
			}

			return !state.application.invalid.hasOwnProperty(fieldName)
		},
		invalidFieldError: (state) => (fieldName) => {
			if (! state.application.invalidDirty.includes(fieldName)) {
				return null
			}

			if (state.application.invalid.hasOwnProperty(fieldName)) {
				return state.application.invalid[fieldName]
			}

			return null
		}
	},

	actions: {
		getInit (context) {
			if (context.state.initialized) {
				// Already fetched
				return
			}

			const url = window.route('init')

			const method = 'GET'
			const data = {}

			return api({
				method,
				url,
				data
			}).then(response => {
				context.commit('setInit', response.data)
			}).catch(err => {
				console.error('Failed to fetch init!')
			})
		},
		refreshInit (context) {
			context.commit('resetInit', {})
			context.dispatch('getInit')
		},
		resetApplication (context) {
			context.state.application = {
				id: null,
				status: 1,
				currentStep: 1,
				currentSubStep: false,
				completedSubSteps: {},
				excludedSubSteps: [],
				submitted: false,
				values: {},
				errors: {},
				invalid: {},
				invalidDirty: [],
				config: {
					effective_date_auto_complete: true
				}
			}
		},
		resetInvalid (context) {
			context.state.application.invalid = {}
			context.state.application.invalidDirty = []
		},
		applicationSubmitted (context) {
			context.commit('setApplicationSubmitted')
		},
		async getApiData ({ commit }, apiKey) {
			const url = window.route('admin.' + apiKey + '.api.index')
			const method = 'GET'

			return api({
				method,
				url
			}).then((response) => {
				commit('setApiData', { key: apiKey, data: response.data })
			})
		},
		resetApiData ({ commit, dispatch }, apiKey) {
			commit('setApiData', { key: apiKey, data: [] })
			dispatch('getApiData', apiKey)
		}
	}
})
